import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { User, Settings, Award, BookOpen, Edit, Share2, Copy, Check, ExternalLink } from 'lucide-react';
import { ServiceCard, CourseCard } from '../components/ManageListings';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [myServices, setMyServices] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isSchool, setIsSchool] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Username editing
  const [showUsernameEdit, setShowUsernameEdit] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Generate personal link - prefer username over ID
  const userIdentifier = user?.username || user?.id;
  const personalLink = userIdentifier ? `${window.location.origin}/p/${userIdentifier}` : '';
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(personalLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  
  // Check username availability with debounce
  useEffect(() => {
    if (!newUsername || newUsername.length < 3) {
      setUsernameAvailable(null);
      setUsernameError('');
      return;
    }
    
    const timer = setTimeout(async () => {
      setUsernameChecking(true);
      try {
        // Pass current user ID to exclude from check (so user can keep their own username)
        const res = await axios.get(`${API_URL}/api/auth/username/check/${newUsername}`, {
          params: { exclude_user: user?.id }
        });
        setUsernameAvailable(res.data.available);
        if (!res.data.available) {
          const reasons = {
            'taken': 'Este nombre ya está en uso',
            'reserved': 'Este nombre está reservado',
            'invalid_format': 'Solo letras, números y guiones (debe empezar con letra)',
            'invalid_length': 'Debe tener entre 3 y 30 caracteres'
          };
          setUsernameError(reasons[res.data.reason] || 'No disponible');
        } else {
          setUsernameError('');
        }
      } catch (err) {
        setUsernameError('Error al verificar');
      }
      setUsernameChecking(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [newUsername, user?.id]);
  
  const handleSaveUsername = async () => {
    if (!usernameAvailable) return;
    
    setSavingUsername(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/auth/username`,
        { username: newUsername },
        { headers: getAuthHeaders() }
      );
      
      // Update local user state
      updateUser({ ...user, username: res.data.username });
      setShowUsernameEdit(false);
      setNewUsername('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errors = {
        'username_taken': 'Este nombre ya está en uso',
        'username_reserved': 'Este nombre está reservado',
        'username_invalid_format': 'Formato inválido'
      };
      setUsernameError(errors[detail] || 'Error al guardar');
    }
    setSavingUsername(false);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = useCallback(async () => {
    try {
      const profilesRes = await axios.get(`${API_URL}/api/auth/profiles`, { headers: getAuthHeaders() });
      const profiles = profilesRes.data;
      
      setIsTeacher(profiles.is_teacher);
      setIsSchool(profiles.is_school);
      
      if (profiles.teacher) setTeacherProfile(profiles.teacher);
      if (profiles.school) setSchoolProfile(profiles.school);
      
      if (profiles.is_teacher) {
        const servicesRes = await axios.get(`${API_URL}/api/services/me/list`, { headers: getAuthHeaders() });
        setMyServices(servicesRes.data);
      }
      
      if (profiles.is_school) {
        const coursesRes = await axios.get(`${API_URL}/api/courses/me/list`, { headers: getAuthHeaders() });
        setMyCourses(coursesRes.data);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  if (!isAuthenticated) return null;

  const hasAvatar = user?.avatar;
  const hasPendingAvatar = user?.avatar_status === 'pending';
  const canBecomeTeacher = !isTeacher;
  const canBecomeSchool = !isSchool;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="profile-page">
      <h1 className="font-serif text-4xl font-bold text-primary mb-8">{t('profile.title')}</h1>

      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-20 h-20 rounded-full bg-cream-dark flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#F5EFE6' }}>
            {hasAvatar ? (
              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <User className="h-10 w-10 text-gold" style={{ color: '#D4A574' }} />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">{user?.name}</h2>
            <p className="text-secondary">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {isTeacher && <Badge className="bg-purple-500 text-white">Profesional</Badge>}
              {isSchool && <Badge className="bg-blue-500 text-white">Escuela</Badge>}
              {!isTeacher && !isSchool && <Badge variant="outline">{user?.role}</Badge>}
              {hasPendingAvatar && (
                <Badge className="bg-yellow-500 text-white text-xs">Photo pending</Badge>
              )}
            </div>
          </div>
          <Link to="/profile/edit">
            <Button variant="outline" className="border-gold text-gold" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="edit-profile-btn">
              <Settings className="h-4 w-4 mr-2" /> {t('profile.editProfile')}
            </Button>
          </Link>
        </div>

        {(canBecomeTeacher || canBecomeSchool) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {canBecomeTeacher && (
              <Link to="/become-teacher">
                <Button variant="outline" className="w-full border-gold text-gold" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="become-teacher-btn">
                  <Award className="h-4 w-4 mr-2" /> {t('profile.becomeTeacher')}
                </Button>
              </Link>
            )}
            {canBecomeSchool && (
              <Link to="/become-school">
                <Button variant="outline" className="w-full border-gold text-gold" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="become-school-btn">
                  <BookOpen className="h-4 w-4 mr-2" /> {t('profile.becomeSchool')}
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Personal Link Card - for teachers and schools */}
      {(isTeacher || isSchool) && (
        <div className="bg-gradient-to-r from-gold/10 to-gold/5 border border-gold/20 rounded-2xl p-6 mb-6" style={{ background: 'linear-gradient(to right, rgba(212, 165, 116, 0.1), rgba(212, 165, 116, 0.05))' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-primary">{t('profile.personalLink', 'Tu enlace personal')}</h3>
              <p className="text-sm text-secondary">{t('profile.personalLinkDesc', 'Comparte este enlace como tu tarjeta de visita digital')}</p>
            </div>
            {!showUsernameEdit && (
              <button
                onClick={() => { setShowUsernameEdit(true); setNewUsername(user?.username || ''); }}
                className="text-sm text-gold hover:underline flex items-center gap-1"
                style={{ color: '#D4A574' }}
              >
                <Edit className="w-4 h-4" />
                {user?.username ? 'Cambiar' : 'Personalizar'}
              </button>
            )}
          </div>
          
          {/* Username Editor */}
          {showUsernameEdit && (
            <div className="mb-4 p-4 bg-white rounded-lg border">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Elige tu nombre de usuario personalizado
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">{window.location.origin}/p/</span>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="tu-nombre"
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gold/50 ${
                    usernameError ? 'border-red-300' : usernameAvailable ? 'border-green-300' : 'border-gray-300'
                  }`}
                  maxLength={30}
                />
              </div>
              
              {/* Status feedback */}
              <div className="mt-2 text-sm">
                {usernameChecking && (
                  <span className="text-gray-500">Verificando disponibilidad...</span>
                )}
                {!usernameChecking && usernameError && (
                  <span className="text-red-500">{usernameError}</span>
                )}
                {!usernameChecking && usernameAvailable && (
                  <span className="text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" /> ¡Disponible!
                  </span>
                )}
              </div>
              
              <p className="mt-2 text-xs text-gray-500">
                Solo letras, números y guiones. Debe empezar con letra. 3-30 caracteres.
              </p>
              
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={handleSaveUsername}
                  disabled={!usernameAvailable || savingUsername}
                  size="sm"
                  className="bg-gold text-white"
                  style={{ backgroundColor: '#D4A574' }}
                >
                  {savingUsername ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button
                  onClick={() => { setShowUsernameEdit(false); setNewUsername(''); setUsernameError(''); }}
                  size="sm"
                  variant="outline"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
          
          {/* Current Link */}
          <div className="flex items-center gap-2 bg-white rounded-lg border p-2">
            <input 
              type="text" 
              value={personalLink} 
              readOnly 
              className="flex-1 bg-transparent text-sm text-gray-600 outline-none px-2"
              data-testid="personal-link-input"
            />
            <Button
              onClick={handleCopyLink}
              size="sm"
              className={`${linkCopied ? 'bg-green-500' : 'bg-gold'} text-white`}
              style={{ backgroundColor: linkCopied ? '#22c55e' : '#D4A574' }}
              data-testid="copy-link-btn"
            >
              {linkCopied ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  {t('common.copied', 'Copiado!')}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  {t('common.copy', 'Copiar')}
                </>
              )}
            </Button>
            <a 
              href={personalLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-gold transition-colors"
              title={t('profile.previewLink', 'Ver mi página')}
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
          
          {!user?.username && !showUsernameEdit && (
            <p className="mt-2 text-xs text-gray-500">
              💡 Personalizza il tuo link per renderlo più facile da ricordare!
            </p>
          )}
        </div>
      )}

      {/* Teacher Section - Services */}
      {isTeacher && teacherProfile && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          {/* Teacher Profile Header */}
          <div className="flex justify-between items-start mb-4 pb-4 border-b">
            <div className="flex items-center gap-3">
              <Award className="h-6 w-6 text-purple-500" />
              <div>
                <h3 className="font-serif text-xl font-semibold">Perfil Profesional</h3>
                <div className="flex items-center gap-2 mt-1">
                  {teacherProfile.is_approved ? (
                    <Badge className="bg-green-500 text-white text-xs">Aprobado</Badge>
                  ) : (
                    <Badge className="bg-yellow-500 text-white text-xs">Pendiente aprobación</Badge>
                  )}
                  {teacherProfile.edit_pending && (
                    <Badge className="bg-orange-500 text-white text-xs">Edición pendiente</Badge>
                  )}
                </div>
              </div>
            </div>
            <Link to={`/edit-teacher/${teacherProfile.id}`}>
              <Button variant="outline" size="sm" className="border-purple-500 text-purple-500" data-testid="edit-teacher-btn">
                <Edit className="h-4 w-4 mr-1" /> Editar Perfil
              </Button>
            </Link>
          </div>
          
          {/* Services */}
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-700">{t('profile.myServices')}</h4>
            <Link to="/create-service">
              <Button className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }} data-testid="create-service-btn">
                {t('profile.createService')}
              </Button>
            </Link>
          </div>
          {myServices.length === 0 ? (
            <p className="text-secondary">{t('common.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myServices.map((service) => (
                <ServiceCard 
                  key={service.id} 
                  service={service} 
                  language={i18n.language}
                  onUpdate={loadData}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* School Section - Courses */}
      {isSchool && schoolProfile && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          {/* School Profile Header */}
          <div className="flex justify-between items-start mb-4 pb-4 border-b">
            <div className="flex items-center gap-3">
              <BookOpen className="h-6 w-6 text-blue-500" />
              <div>
                <h3 className="font-serif text-xl font-semibold">Perfil Escuela</h3>
                <div className="flex items-center gap-2 mt-1">
                  {schoolProfile.is_approved ? (
                    <Badge className="bg-green-500 text-white text-xs">Aprobado</Badge>
                  ) : (
                    <Badge className="bg-yellow-500 text-white text-xs">Pendiente aprobación</Badge>
                  )}
                  {schoolProfile.edit_pending && (
                    <Badge className="bg-orange-500 text-white text-xs">Edición pendiente</Badge>
                  )}
                </div>
              </div>
            </div>
            <Link to={`/edit-school/${schoolProfile.id}`}>
              <Button variant="outline" size="sm" className="border-blue-500 text-blue-500" data-testid="edit-school-btn">
                <Edit className="h-4 w-4 mr-1" /> Editar Perfil
              </Button>
            </Link>
          </div>
          
          {/* Courses */}
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-700">{t('profile.myCourses')}</h4>
            <Link to="/create-course">
              <Button className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }} data-testid="create-course-btn">
                {t('profile.createCourse')}
              </Button>
            </Link>
          </div>
          {myCourses.length === 0 ? (
            <p className="text-secondary">{t('common.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myCourses.map((course) => (
                <CourseCard 
                  key={course.id} 
                  course={course} 
                  language={i18n.language}
                  onUpdate={loadData}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
